import { Component, DestroyRef, OnInit } from '@angular/core';
import * as MenuActions from '@states/menu/menu.actions';
import { MenuKey } from '@enums/menu.enum';
import { select, Store } from '@ngrx/store';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { catchError, Observable, of } from 'rxjs';
import { LocationEditSelectors } from '@states/location-edit/location-edit.selector-types';
import { ComplianceOption, LocationModel } from '../../../locations/location.model';
import { LocationsService } from '../../../locations/locations.service';
import { SharedActions } from '@states/shared/shared.action-types';
import { ofType } from '@ngrx/effects';
import { LocationEditActions } from '@states/location-edit/location-edit.action-types';
import { LocationEditEffects } from '@effects/location-edit.effects';
import { LocationActions } from '@states/location/location.action-types';
import { PreloaderColor } from '@enums/shared.enum';
import { Edge } from 'src/app/edge/edge.model';
import { EdgeSelectors } from '@states/edge/edge.selector-types';
import { LocationSelectors } from '@states/location/location.selector-types';
import { UserSelectors } from '@states/user/user.selector-types';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { LetDirective } from '@ngrx/component';
import { MatOption, MatSelect } from '@angular/material/select';
import { NgForOf, TitleCasePipe } from '@angular/common';
import { UiKitModule } from '../../../shared/ui-kit/ui-kit.module';
import { ConfirmDialogModel, ConfirmDialogService, UiToggleComponent } from '@ui';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-location-configuration',
  templateUrl: './location-configuration.component.html',
  styleUrls: ['./location-configuration.component.scss'],
  imports: [
    LetDirective,
    ReactiveFormsModule,
    TranslatePipe,
    MatSelect,
    NgForOf,
    TitleCasePipe,
    MatOption,
    UiKitModule,
    UiToggleComponent,
  ],
})
export class LocationConfigurationComponent implements OnInit {
  public NetworkProfile = LocationModel.NetworkProfile;

  public selectIsDeveloper$: Observable<boolean> = this.store$.pipe(select(UserSelectors.isDeveloper));

  public editLocationFormGroup: UntypedFormGroup;
  locationId: string;

  public PreloaderColor = PreloaderColor;
  public ComplianceOption = ComplianceOption;
  public ComplianceOptionKeys = Object.keys(ComplianceOption).filter((key) => !isNaN(Number(key)));

  public origNetworkProfile = LocationModel.NetworkProfile.HighQuality;

  constructor(
    private store$: Store,
    private fb: UntypedFormBuilder,
    private locationsService: LocationsService,
    private locationEditEffects: LocationEditEffects,
    private translateService: TranslateService,
    private confirm: ConfirmDialogService,
    private destroyRef: DestroyRef,
  ) {}

  public get edges$(): Observable<Edge.EdgeDocument[]> {
    return this.locationId
      ? this.store$.pipe(select(EdgeSelectors.selectLocationEdgesByLocationId(this.locationId)))
      : of([]);
  }

  public get compliance$(): Observable<{ faceRecognition: boolean; genderClassification: boolean }> {
    return this.locationId
      ? this.store$.pipe(select(LocationSelectors.selectLocationComplianceById(this.locationId)))
      : of({ faceRecognition: true, genderClassification: true });
  }

  ngOnInit(): void {
    this.store$.dispatch(
      MenuActions.setActiveLevel2Menu({
        activeLevel2: MenuKey.configuration,
      }),
    );

    this.editLocationFormGroup = this.fb.group({
      _id: ['', []],
      complianceOption: ['0'],
      genderClassification: [true, []],
      faceRecognition: [true, []],
      networkProfile: [LocationModel.NetworkProfile.HighQuality, []],
    });

    this.store$
      .pipe(select(LocationEditSelectors.selectedLocation), takeUntilDestroyed(this.destroyRef))
      .subscribe((location) => {
        if (!!location) {
          this.locationId = location._id;
          this.editLocationFormGroup.patchValue({
            _id: location?._id,
            complianceOption: location.complianceOption ?? '0',
            genderClassification: location.genderClassification ?? true,
            faceRecognition: location.faceRecognition ?? true,
            networkProfile: location?.networkProfile ?? LocationModel.NetworkProfile.HighQuality,
          });
          this.origNetworkProfile = location?.networkProfile;
        }
      });

    this.editLocationFormGroup
      .get('complianceOption')
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (+value === ComplianceOption.INDEPENDENT) {
          this.editLocationFormGroup.patchValue({
            genderClassification: true,
            faceRecognition: true,
          });
        } else {
          this.editLocationFormGroup.patchValue({
            genderClassification: false,
            faceRecognition: false,
          });
        }
      });

    this.locationEditEffects.pressSave$
      .pipe(takeUntilDestroyed(this.destroyRef), ofType(LocationEditActions.pressSave))
      .subscribe((_) => {
        this.store$.dispatch(SharedActions.setIsSaving({ isSaving: true }));
        if (this.origNetworkProfile !== this.editLocationFormGroup?.value?.networkProfile) {
          this.confirm
            .open({
              title: this.translateService.instant('ADD_LOCATION.UPDATE_CAMERAS_TITLE'),
              type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
              msg: this.translateService.instant('ADD_LOCATION.UPDATE_CAMERAS_MESSAGE'),
              confirm: this.translateService.instant('ADD_LOCATION.UPDATE_CAMERAS_YES'),
              cancel: this.translateService.instant('ADD_LOCATION.UPDATE_CAMERAS_NO'),
            })
            .subscribe((result) => {
              if (result.selection === ConfirmDialogModel.ConfirmResult.OK) {
                this.editLocation(true);
              } else if (result.selection === ConfirmDialogModel.ConfirmResult.CANCEL) {
                this.editLocation();
              }
            });
        } else {
          this.editLocation();
        }
      });
  }

  editLocation(updateNetworkProfile = false) {
    const location = this.editLocationFormGroup.value;
    const request: LocationModel.LocationCreateRequest = {
      ...location,
    };
    if (updateNetworkProfile) {
      request.updateNetworkProfile = true;
    }
    return this.locationsService
      .updateLocation(request)
      .pipe(
        catchError((err) => {
          this.store$.dispatch(SharedActions.setIsSaving({ isSaving: false }));
          return err;
        }),
      )
      .subscribe((res) => {
        this.store$.dispatch(
          LocationActions.CreateLocationNoBackendCall({
            request: {
              ...request,
              _id: location._id,
            },
          }),
        );
        this.store$.dispatch(
          SharedActions.showMessage({
            success: this.translateService.instant('TOASTS.UPDATED_SUCCESS', { entity: 'Location' }),
          }),
        );
        this.store$.dispatch(SharedActions.setIsSaving({ isSaving: false }));
      });
  }
}
